import { Trans, useLingui } from '@lingui/react/macro'
import { AddPropertyModal } from '$src/components/profile/AddPropertyModal'
import { CustomerCard } from '$src/components/profile/CustomerCard'
import { LanguageSettings } from '$src/components/profile/LanguageSettings'
import { NotificationSettings } from '$src/components/profile/NotificationSettings'
import { PersonalInfoCard } from '$src/components/profile/PersonalInfoCard'
import { usePropertyCreation } from '$src/lib/hooks/usePropertyCreation'
import { authHooks, customerHooks, userHooks } from '$src/lib/trpcHooks'
import {
  Avatar,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Stack,
  Switch,
  Text,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { AppPage, useDeveloperMode } from '@markku/components'
import {
  IconCode,
  IconHome,
  IconLogout,
  IconPlus,
  IconUserCircle,
} from '@tabler/icons-react'
import { createFileRoute } from '@tanstack/react-router'

function ProfilePage() {
  const { t } = useLingui()
  const { data: user } = userHooks.useMe()
  const { isDeveloperMode, toggleDeveloperMode } = useDeveloperMode()
  const { mutate: logout } = authHooks.useLogout()
  const { data: customersData } = customerHooks.useList()
  const customers = customersData?.data || []
  const { handlePropertyCreated, WelcomeModal } = usePropertyCreation()
  const [
    addFirstPropertyOpened,
    { open: openAddFirstProperty, close: closeAddFirstProperty },
  ] = useDisclosure(false)

  const handlePropertyCreatedAndCloseModal = (
    propertyId: number,
    propertyAddress: string,
  ) => {
    closeAddFirstProperty()
    handlePropertyCreated(propertyId, propertyAddress)
  }

  const handleLogout = () => {
    modals.openConfirmModal({
      title: t`Log out`,
      centered: true,
      children: (
        <Text>
          <Trans>Are you sure you want to log out?</Trans>
        </Text>
      ),
      labels: { confirm: t`Log out`, cancel: t`Cancel` },
      confirmProps: { color: 'red' },
      onConfirm: () => logout(),
    })
  }

  return (
    <AppPage title={t`Profile`} backTarget="/">
      <Stack gap="xl" pb={80}>
        {user && (
          <>
            {/* Profile Header */}
            <Card
              radius="lg"
              p="lg"
              style={{
                background: 'linear-gradient(135deg, #FFF9F0 0%, #FFE8CC 100%)',
                border: 'none',
              }}
            >
              <Group align="center" gap="md">
                <Avatar
                  size={60}
                  radius="xl"
                  color="brandOrange"
                  styles={{
                    placeholder: {
                      backgroundColor: 'var(--mantine-color-brandOrange-6)',
                      color: 'white',
                    },
                  }}
                >
                  <IconUserCircle size={35} stroke={1.5} />
                </Avatar>
                <Stack gap={0} style={{ flex: 1 }}>
                  <Text size="lg" fw={600}>
                    {user.firstName} {user.lastName}
                  </Text>
                  <Text c="dimmed">{user.email}</Text>
                </Stack>
              </Group>
            </Card>

            {/* Language Settings */}
            <LanguageSettings />

            {/* Personal Info Card */}
            <PersonalInfoCard user={user} />

            {/* Notification Settings */}
            <NotificationSettings />

            {/* Customer Cards or Empty State */}
            {customers?.length ? (
              customers.map((customer) => (
                <CustomerCard key={customer.id} customer={customer} />
              ))
            ) : (
              <Card
                radius="md"
                withBorder
                onClick={openAddFirstProperty}
                style={{
                  cursor: 'pointer',
                  borderColor: 'var(--mantine-color-brandOrange-2)',
                  transition: 'all 0.2s',
                }}
                styles={{
                  root: {
                    '&:hover': {
                      borderColor: 'var(--mantine-color-brandOrange-4)',
                      transform: 'translateY(-2px)',
                    },
                  },
                }}
              >
                <Stack gap="md" align="center" py="xl">
                  <Box
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'var(--mantine-color-brandOrange-0)',
                    }}
                  >
                    <IconHome
                      size={30}
                      color="var(--mantine-color-brandOrange-6)"
                    />
                  </Box>
                  <Stack gap={4} align="center">
                    <Text fw={600} size="lg">
                      <Trans>Add your first property</Trans>
                    </Text>
                    <Text c="dimmed" ta="center">
                      <Trans>
                        Start by adding a property for the maintenance log
                      </Trans>
                    </Text>
                  </Stack>
                  <Button
                    leftSection={<IconPlus size={16} />}
                    variant="light"
                    color="brandOrange"
                  >
                    <Trans>Add property</Trans>
                  </Button>
                </Stack>
              </Card>
            )}

            <Divider my="md" />

            {/* Developer Settings */}
            {user.email?.endsWith('@markku.fi') && (
              <Card radius="md" withBorder>
                <Group justify="space-between" align="center">
                  <Group gap="md">
                    <Box
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'var(--mantine-color-gray-0)',
                      }}
                    >
                      <IconCode size={20} color="var(--mantine-color-dark-7)" />
                    </Box>
                    <Stack gap={0}>
                      <Text fw={600}>
                        <Trans>Developer settings</Trans>
                      </Text>
                      <Text c="dimmed" size="sm">
                        <Trans>Show placeholder content</Trans>
                      </Text>
                    </Stack>
                  </Group>
                  <Switch
                    checked={isDeveloperMode}
                    onChange={() => toggleDeveloperMode()}
                    size="md"
                  />
                </Group>
              </Card>
            )}

            <Divider my="md" />
          </>
        )}

        {/* Logout */}
        <Card
          radius="md"
          withBorder
          onClick={handleLogout}
          style={{
            cursor: 'pointer',
            borderColor: 'var(--mantine-color-red-2)',
            transition: 'all 0.2s',
          }}
          styles={{
            root: {
              '&:hover': {
                borderColor: 'var(--mantine-color-red-4)',
              },
            },
          }}
        >
          <Group gap="md">
            <Box
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--mantine-color-red-0)',
              }}
            >
              <IconLogout size={20} color="var(--mantine-color-red-6)" />
            </Box>
            <Text fw={600} c="red">
              <Trans>Log out</Trans>
            </Text>
          </Group>
        </Card>
      </Stack>

      <AddPropertyModal
        opened={addFirstPropertyOpened}
        onClose={closeAddFirstProperty}
        onPropertyCreated={handlePropertyCreatedAndCloseModal}
      />

      {WelcomeModal}
    </AppPage>
  )
}

export const Route = createFileRoute('/_app/profile')({
  component: ProfilePage,
})
